import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCollectionEmailSend } from '../hooks/useCollectionEmailSend';
import { CollectionEmailSendStatus } from '../model';

interface Props {
  open: boolean;
  emailSendId: number | null;
  onClose: () => void;
  emailType?: 'collection' | 'statement';
  location?: string;
}

// email.body is HTML generated server-side from admin-authored templates — not user-supplied freeform content.
// sandbox="" on the iframe provides defense-in-depth.
const buildPreviewHtml = (body: string): string => `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <style>
      body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4; }
    </style>
  </head>
  <body>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4;">
      <tr>
        <td align="center" style="padding:24px 16px;">
          <table role="presentation" width="600" cellpadding="0" cellspacing="0"
                 style="max-width:600px;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
            <tr>
              <td style="padding:0 40px;">
                <hr style="border:none;border-top:2px solid #96052F;margin:0;" />
              </td>
            </tr>
            <tr>
              <td style="padding:32px 40px;color:#333;font-size:15px;line-height:1.7;font-family:Arial,Helvetica,sans-serif;">
                ${body}
              </td>
            </tr>
            <tr>
              <td style="background:#96052F;padding:20px 40px;text-align:center;">
                <p style="margin:0;color:#fff;font-size:13px;font-family:Arial,Helvetica,sans-serif;">
                  TUDI Collections &bull; <a href="mailto:ar@tudi.com" style="color:#fff;text-decoration:underline;">ar@tudi.com</a>
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>
`;

const statusChipProps = (
  status: CollectionEmailSendStatus,
): { label: string; color: 'success' | 'error' | 'default' } => {
  if (status === 'sent') return { label: 'Sent', color: 'success' };
  if (status === 'failed') return { label: 'Failed', color: 'error' };
  return { label: 'Queued', color: 'default' };
};

const EmailHistoryDetailModal: React.FC<Props> = ({
  open,
  emailSendId,
  onClose,
  emailType = 'collection',
  location,
}) => {
  const { data, isLoading, isError } = useCollectionEmailSend(open ? emailSendId : null, emailType, location);
  const email = data?.data ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{email ? email.company : 'Email Detail'}</Typography>
          {email && <Chip {...statusChipProps(email.status)} size="small" />}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load email details.
          </Alert>
        )}
        {email && (
          <>
            {email.status === 'failed' && email.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {email.error_message}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Subject:</strong> {email.subject}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>To:</strong>
                </Typography>
                {email.to_emails && email.to_emails.length > 0 ? (
                  email.to_emails.map((addr) => <Chip key={addr} label={addr} size="small" />)
                ) : (
                  <Chip label="No email addresses" size="small" color="error" variant="outlined" />
                )}
              </Box>
              {email.queued_at && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Queued:</strong> {email.queued_at}
                </Typography>
              )}
              {email.delivered_at && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Delivered:</strong> {email.delivered_at}
                </Typography>
              )}
              {email.send_source === 'manual' && email.triggered_by_name && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  <strong>Triggered by:</strong> {email.triggered_by_name}
                </Typography>
              )}
              {email.attachment_metadata && email.attachment_metadata.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Attachments:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {email.attachment_metadata.map((a) => (
                      <Chip key={a.edoc_invoice_import_id} label={a.filename} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* sandbox="" is intentionally maximally restrictive — this is a read-only preview of the email body. */}
            <iframe
              srcDoc={buildPreviewHtml(email.body)}
              sandbox=""
              title="Email preview"
              style={{ width: '100%', height: 400, border: '1px solid #e0e0e0', borderRadius: 4 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailHistoryDetailModal;
